'use client';

import './styles/default.scss';
import type { RefObject } from 'react';
import type React from 'react';
import { useRef, useEffect, useCallback, useState } from 'react';
import type { JSX } from 'react';
import { VideoPlayer } from './video-player';
import { usePageVideoCoordinator } from './page-video-coordinator';
import { detectVideoTypeFromSource } from './video.utils';
import { VideoDetailsWrapper } from '@/app/elements/video/subcomponents/video-details-wrapper/video-details-wrapper';
import type { SocialTagBadgeState } from '@/types/games';
import { VideoSoundButtonComponent } from '@/app/elements/video/video-sound-button';
import type Player from 'video.js/dist/types/player';
import { useMutedState } from '@/app/elements/video/video-context';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import type { LauncherUrlProps } from '@/utils/url/launcher-url';
import { useShouldShowNotification } from '@/hooks/use-show-notification-icon';
import { useStore } from '@/store/store';
import { trackGameVideoEvent } from '@/tracking/amplitude/events/general/game-video';
import { classes } from '@/utils/classes/classes';

type VideoProps = {
    uid: string;
    src: string;
    videoId: string;
    disableGameLauncher?: boolean;
    hideSoundButton?: boolean;
    videoOptions?: Record<string, unknown>;
    launchParameters?: Record<string, unknown>;
    amplitudeEvents?: Record<string, unknown>;
    alchemerEvent?: boolean;
    videosPlaying?: boolean;
    inPopup?: boolean;
    contextError?: string;
    multipleVideos?: boolean;
    poster?: string;
    title?: string;
    info?: string;
    legal?: string;
    hasVideoDetails?: boolean;
    exploreLogoUrl?: string;
    exploreBackgroundUrl?: string;
    detailsLayout?: string;
    socialTagBadgeState?: SocialTagBadgeState;
    uniquePlayersCount?: number;
    skeleton?: JSX.Element;
    launcherUrlProps?: LauncherUrlProps;
    isVertical?: boolean;
    isNew?: boolean;
    onVideoCanPlay?: () => void;
    onDurationChange?: (seconds: number) => void;
    /** Eligible to WIN the coordinator's election — losers can still play via `sharesPlayback`. */
    isPlaybackCandidate?: boolean;
    /** Plays muted alongside a winner from the same `playbackGroup`. Both sides must opt in. */
    sharesPlayback?: boolean;
    /** Scopes `sharesPlayback`, so unrelated videos never play together. */
    playbackGroup?: string;
};

const getVideoOptions = (src: string, poster: string) => ({
    autoplay: false,
    controls: false,
    muted: true,
    fluid: true,
    responsive: true,
    preload: 'auto',
    sources: [
        {
            src: src,
            type: detectVideoTypeFromSource(src)
        }
    ],
    poster: poster,
    playsinline: true,
    children: ['PosterImage', 'MediaLoader']
});

const pauseCondition = (videoRef: RefObject<Player | null>, videosPlaying?: boolean, windowBlurred?: boolean) =>
    videoRef?.current && (!videosPlaying || windowBlurred);

const playCondition = (
    videoRef: RefObject<Player | null>,
    inPopup?: boolean,
    videosPlaying?: boolean,
    isInView?: boolean,
    windowBlurred?: boolean
) => videoRef?.current && (inPopup || videosPlaying) && isInView && !windowBlurred;

// TODO: refactor this entire file and affected features
export const Video = ({
    uid,
    src,
    videosPlaying,
    inPopup,
    videoId,
    contextError,
    poster = '',
    title,
    info,
    legal,
    hasVideoDetails = true,
    exploreLogoUrl,
    exploreBackgroundUrl,
    detailsLayout,
    socialTagBadgeState,
    uniquePlayersCount,
    skeleton,
    launcherUrlProps,
    isVertical = false,
    hideSoundButton = false,
    isNew = false,
    onVideoCanPlay,
    onDurationChange,
    isPlaybackCandidate,
    sharesPlayback,
    playbackGroup
}: VideoProps) => {
    const { isMuted, setIsMuted } = useMutedState();
    const pageVideoCoordinator = usePageVideoCoordinator(
        videoId,
        isPlaybackCandidate ?? true,
        sharesPlayback,
        playbackGroup
    );
    const effectivePlaying = pageVideoCoordinator ? pageVideoCoordinator.isPlaying : videosPlaying;
    const effectiveMuted = pageVideoCoordinator ? !pageVideoCoordinator.soundEnabled : isMuted;
    const videoRef = useRef<Player>(null);
    const isInView = useRef(false);
    const [showSkeleton, setShowSkeleton] = useState(!!skeleton);
    const observerVideoElement = useRef<IntersectionObserver | null>(null);
    const shouldShowNotificationIcon = useShouldShowNotification(isNew);
    const { sendEvent } = useStore((state) => state.tracking);
    const divRef = useRef<HTMLDivElement>(null);
    const hasSentAutoplayEvent = useRef(false);
    const [windowBlurred, setWindowBlurred] = useState(false);
    // The play observer is created once (in handlePlayerReady) and closes over stale values, so it
    // reads the current play decision through this ref instead.
    const effectivePlayingRef = useRef(effectivePlaying);
    useEffect(() => {
        effectivePlayingRef.current = effectivePlaying;
    }, [effectivePlaying]);

    const updatePlayingStatus = useCallback(
        (forcePlay?: boolean) => {
            if (isVertical) return;

            const player = videoRef.current;
            if (!player) return;

            if (forcePlay) {
                player.play();
            } else if (isInView.current) {
                if (playCondition(videoRef, inPopup, effectivePlayingRef.current, isInView.current, windowBlurred)) {
                    player.play();
                }
            } else if (Number(player.currentTime()) > 0 && !player.paused()) {
                player.pause();
            }
        },
        [inPopup, windowBlurred, isVertical]
    );

    const observeVideoElement = useCallback(() => {
        if (isVertical) return;

        const targetNode = document.getElementById(videoId);
        if (!targetNode) return;

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            isInView.current = entries[0]?.isIntersecting ?? false;
            updatePlayingStatus();
        };

        const observer = new IntersectionObserver(handleIntersection, { threshold: 0.99 });
        observer.observe(targetNode);
        observerVideoElement.current = observer;
    }, [isVertical, videoId, updatePlayingStatus]);

    const handlePlayerReady = useCallback(
        (player: Player) => {
            videoRef.current = player;
            hasSentAutoplayEvent.current = false;
            setShowSkeleton(false);

            if (isVertical) {
                player.on('ended', () => {
                    const videoElement = document.getElementById(videoId);
                    const slide = videoElement?.closest('.video-slide');
                    const isActive = slide?.classList.contains('video-slide-active');
                    if (isActive) {
                        player?.currentTime?.(0);
                        player?.play?.();
                    }
                });
            } else {
                setIsMuted(!!player.muted());
                player.on('volumechange', () => setIsMuted(!!player.muted()));
                player.on('play', () => {
                    if (!hasSentAutoplayEvent.current) {
                        hasSentAutoplayEvent.current = true;
                        const result = trackGameVideoEvent('gameVideoAutoPlayed', divRef);
                        const { sendEvent: currentSendEvent } = useStore.getState().tracking;
                        currentSendEvent([result.event]);
                    }
                });
                player.on('ended', () => {
                    const result = trackGameVideoEvent('gameVideo100pcViewed', divRef);
                    const { sendEvent: currentSendEvent } = useStore.getState().tracking;
                    currentSendEvent([result.event]);
                    player.play();
                });
                if (onDurationChange) {
                    player.on('loadedmetadata', () => onDurationChange(Number(player.duration()) || 0));
                }
                observeVideoElement();
            }
        },
        [isVertical, videoId, observeVideoElement, setIsMuted, onDurationChange]
    );

    const handleSoundClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
            event.stopPropagation();
            event.preventDefault();
            const trackToggle = (muted: boolean) =>
                sendEvent([trackGameVideoEvent(muted ? 'gameVideoMuted' : 'gameVideoUnmuted', divRef).event]);

            if (pageVideoCoordinator) {
                const wasEnabled = pageVideoCoordinator.soundEnabled;
                const didToggle = pageVideoCoordinator.toggleSound();
                // A paused video's claim is a no-op, so tracking it would report a toggle that never happened.
                if (didToggle) {
                    trackToggle(wasEnabled);
                }
                return;
            }
            if (videoRef.current) {
                const player = videoRef.current;
                const newMutedState = !player.muted();
                trackToggle(newMutedState);
                player.muted(newMutedState);
                setIsMuted(newMutedState);
            }
        },
        [setIsMuted, sendEvent, pageVideoCoordinator]
    );

    useEffect(() => {
        if (isVertical) return;

        if (pauseCondition(videoRef, effectivePlaying, windowBlurred)) {
            videoRef.current?.pause();
        }
        if (playCondition(videoRef, inPopup, effectivePlaying, isInView.current, windowBlurred)) {
            videoRef.current?.play();
        }
    }, [effectivePlaying, windowBlurred, inPopup, isVertical]);

    useEffect(() => {
        if (isVertical) return;
        videoRef.current?.muted?.(effectiveMuted);
    }, [effectiveMuted, isVertical]);

    useEffect(() => {
        return () => {
            observerVideoElement.current?.disconnect?.();
        };
    }, []);

    useEffect(() => {
        const handler = (event: Event) => {
            setWindowBlurred(event.type === 'blur');
        };

        window.addEventListener('blur', handler);
        window.addEventListener('focus', handler);

        return () => {
            window.removeEventListener('blur', handler);
            window.removeEventListener('focus', handler);
        };
    }, []);

    return (
        <div className="video-wrapper" ref={divRef}>
            {showSkeleton && skeleton}

            <div
                className={classes(
                    'video-wrapper__inner',
                    showSkeleton && 'video-wrapper__hidden',
                    isVertical && 'video-wrapper__vertical'
                )}>
                <div id={videoId} className="video">
                    <VideoPlayer
                        options={getVideoOptions(src, poster)}
                        onReady={handlePlayerReady}
                        contextError={contextError}
                        videoId={videoId}
                        shouldShowSpinner={!isVertical}
                        suppressNativePoster={isVertical}
                        onVideoCanPlay={onVideoCanPlay}
                    />
                    {launcherUrlProps && (
                        <GameUrl launcherUrlProps={launcherUrlProps}>
                            <div className="video-overlay-wrapper">
                                <div className="video-overlay" />
                            </div>
                        </GameUrl>
                    )}
                    {!hideSoundButton && (
                        <VideoSoundButtonComponent isMuted={effectiveMuted} onButtonClick={handleSoundClick} />
                    )}
                </div>
                {hasVideoDetails && (
                    <VideoDetailsWrapper
                        logo={exploreLogoUrl}
                        background={exploreBackgroundUrl}
                        title={title}
                        info={info}
                        legal={legal}
                        detailsLayout={detailsLayout}
                        uid={uid}
                        socialTagBadgeState={socialTagBadgeState}
                        uniquePlayersCount={uniquePlayersCount}
                        launcherUrlProps={launcherUrlProps}
                        showNotificationIcon={shouldShowNotificationIcon}
                    />
                )}
            </div>
        </div>
    );
};
